import { FC, useEffect, useMemo } from "react";
import {
  Button,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Badge,
  Alert,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { IconCheck, IconAlertCircle } from "@tabler/icons-react";
import { Page } from "../../../Components/Page";
import { useRequiredParams } from "../../../hooks/useRequiredParams";
import { useCompetition } from "../../../hooks/useCompetition";
import { useGameList } from "../../../hooks/useGameList";
import { useTeamList } from "../../../hooks/useTeamList";
import { gameSetActualTeams } from "../../../openapi/api-client";
import { ResolvedTeamReference } from "../../../Components/ResolvedTeamReference";
import { teamRefToTeamId } from "../../../utils/teamRefToTeamId";
interface GameTeamSelection {
  homeTeamId: string;
  guestTeamId: string;
}

export const SetMatchdayTeams: FC = () => {
  const { competitionId, matchdayId } = useRequiredParams([
    "competitionId",
    "matchdayId",
  ]);
  const navigate = useNavigate();

  const competition = useCompetition(competitionId);
  const games = useGameList(competitionId);
  const teams = useTeamList();

  const matchday = useMemo(
    () => competition.data?.matchdays.find((m) => m.id === matchdayId),
    [competition.data, matchdayId],
  );

  const matchdayGames = useMemo(
    () =>
      games.data?.games
        .filter((g) => g.matchdayId === matchdayId)
        .sort(
          (a, b) =>
            new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime(),
        ) ?? [],
    [games.data, matchdayId],
  );

  const gamesNeedingTeams = useMemo(
    () =>
      matchdayGames.filter(
        (g) => !g.actualTeams.homeTeamId || !g.actualTeams.guestTeamId,
      ),
    [matchdayGames],
  );

  const enrolledTeams = useMemo(
    () =>
      teams.data?.teams.filter((t) =>
        competition.data?.teamIds.includes(t.id),
      ) ?? [],
    [teams.data, competition.data],
  );

  const recommendedTeams = useQuery({
    queryKey: [
      "matchdayRecommendedTeams",
      matchdayId,
      gamesNeedingTeams.map((g) => g.id),
    ],
    queryFn: async () => {
      const results: Record<
        string,
        { homeTeamId?: string; guestTeamId?: string }
      > = {};

      for (const game of gamesNeedingTeams) {
        const homeTeamId =
          game.actualTeams.homeTeamId ??
          (await teamRefToTeamId(game.plannedTeams.home, competitionId));
        const guestTeamId =
          game.actualTeams.guestTeamId ??
          (await teamRefToTeamId(game.plannedTeams.guest, competitionId));

        results[game.id] = { homeTeamId, guestTeamId };
      }

      return results;
    },
    enabled: gamesNeedingTeams.length > 0,
  });

  const form = useForm<{ selections: Record<string, GameTeamSelection> }>({
    initialValues: {
      selections: {},
    },
  });

  useEffect(() => {
    if (gamesNeedingTeams.length === 0) {
      return;
    }

    const newSelections: Record<string, GameTeamSelection> = {};

    for (const game of gamesNeedingTeams) {
      const recommended = recommendedTeams.data?.[game.id];
      newSelections[game.id] = {
        homeTeamId:
          game.actualTeams.homeTeamId ?? recommended?.homeTeamId ?? "",
        guestTeamId:
          game.actualTeams.guestTeamId ?? recommended?.guestTeamId ?? "",
      };
    }

    form.setValues({ selections: newSelections });
  }, [gamesNeedingTeams, recommendedTeams.data]);

  const setTeamsMutation = useMutation({
    mutationFn: async (
      selections: Record<string, GameTeamSelection>,
    ): Promise<void> => {
      for (const [gameId, selection] of Object.entries(selections)) {
        const game = gamesNeedingTeams.find((g) => g.id === gameId);
        if (!game) continue;

        const needsHome = !game.actualTeams.homeTeamId && selection.homeTeamId;
        const needsGuest =
          !game.actualTeams.guestTeamId && selection.guestTeamId;

        if (needsHome || needsGuest) {
          await gameSetActualTeams({
            id: gameId,
            homeTeamId: needsHome ? selection.homeTeamId : undefined,
            guestTeamId: needsGuest ? selection.guestTeamId : undefined,
          });
        }
      }
    },
    onSuccess: () => {
      void navigate(`/control/competitions/${competitionId}/groups`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void setTeamsMutation.mutateAsync(form.values.selections);
  };

  const teamOptions = enrolledTeams.map((t) => ({
    label: t.name,
    value: t.id,
  }));

  const allGamesComplete = gamesNeedingTeams.every((game) => {
    const selection = form.values.selections[game.id];
    if (!selection) return false;

    const homeOk = game.actualTeams.homeTeamId ?? selection.homeTeamId;
    const guestOk = game.actualTeams.guestTeamId ?? selection.guestTeamId;

    return homeOk && guestOk;
  });

  const isLoading =
    !competition.isSuccess || !games.isSuccess || !teams.isSuccess;

  if (isLoading) {
    return (
      <Page title="Teams festlegen">
        <Group justify="center" p="xl">
          <Loader />
        </Group>
      </Page>
    );
  }

  if (!matchday) {
    return (
      <Page title="Teams festlegen">
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Spieltag nicht gefunden.
        </Alert>
      </Page>
    );
  }

  const goBack = () => {
    void navigate(`/control/competitions/${competitionId}/groups`);
  };

  return (
    <Page title={`Teams festlegen: ${matchday.name}`}>
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={3}>{matchday.name}</Title>
            <Text c="dimmed" size="sm">
              {matchdayGames.length} Spiele, davon {gamesNeedingTeams.length}{" "}
              ohne festgelegte Mannschaften
            </Text>
          </div>
          <Button variant="default" onClick={goBack}>
            Zurück
          </Button>
        </Group>

        {gamesNeedingTeams.length === 0 ? (
          <Alert color="green" icon={<IconCheck size={16} />}>
            Alle Spiele dieses Spieltags haben bereits festgelegte Mannschaften.
          </Alert>
        ) : !recommendedTeams.isSuccess ? (
          <Group justify="center" p="xl">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Lade Vorschläge...
            </Text>
          </Group>
        ) : (
          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <Table.ScrollContainer minWidth={800}>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Spiel</Table.Th>
                      <Table.Th>Zeit</Table.Th>
                      <Table.Th>Heim (geplant)</Table.Th>
                      <Table.Th>Heim</Table.Th>
                      <Table.Th>Gast (geplant)</Table.Th>
                      <Table.Th>Gast</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {gamesNeedingTeams.map((game) => {
                      const selection = form.values.selections[game.id];
                      if (!selection) return null;

                      return (
                        <Table.Tr key={game.id}>
                          <Table.Td>
                            <Text fw={500}>{game.name}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge variant="light" color="gray">
                              {new Date(game.dateTime).toLocaleString("de-DE", {
                                day: "2-digit",
                                month: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c="dimmed">
                              <ResolvedTeamReference
                                teamReference={game.plannedTeams.home}
                                competitionId={competitionId}
                              />
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Select
                              placeholder="Team auswählen"
                              disabled={!!game.actualTeams.homeTeamId}
                              clearable
                              searchable
                              size="sm"
                              nothingFoundMessage="Nicht gefunden"
                              data={teamOptions}
                              value={selection.homeTeamId}
                              onChange={(value) => {
                                form.setFieldValue(
                                  `selections.${game.id}.homeTeamId`,
                                  value ?? "",
                                );
                              }}
                            />
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c="dimmed">
                              <ResolvedTeamReference
                                teamReference={game.plannedTeams.guest}
                                competitionId={competitionId}
                              />
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Select
                              placeholder="Team auswählen"
                              disabled={!!game.actualTeams.guestTeamId}
                              clearable
                              searchable
                              size="sm"
                              nothingFoundMessage="Nicht gefunden"
                              data={teamOptions}
                              value={selection.guestTeamId}
                              onChange={(value) => {
                                form.setFieldValue(
                                  `selections.${game.id}.guestTeamId`,
                                  value ?? "",
                                );
                              }}
                            />
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>

              <Group justify="flex-end">
                <Button variant="default" onClick={goBack}>
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  loading={setTeamsMutation.isPending}
                  disabled={!allGamesComplete}
                >
                  Alle Teams festlegen ({gamesNeedingTeams.length})
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Stack>
    </Page>
  );
};
